import React, { useState } from 'react';
import {
  LogOut,
  Menu,
  X,
  Settings
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { DashboardIcon, ClientsIcon, ProjectsIcon, TeamIcon, AdminIcon } from './NavigationIcons';
import { UserSettingsModal } from './UserSettingsModal';

interface NavigationProps {
  activeView: string;
  onNavigate: (view: string) => void;
}

const allNavigationItems = [
  { id: 'dashboard', label: 'Dashboard', Icon: DashboardIcon },
  { id: 'clients', label: 'Clients', Icon: ClientsIcon },
  { id: 'projects', label: 'Projects', Icon: ProjectsIcon },
  { id: 'team', label: 'Team', Icon: TeamIcon },
  { id: 'admin', label: 'Admin', Icon: AdminIcon },
];

export const Navigation: React.FC<NavigationProps> = ({ activeView, onNavigate }) => {
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Get nav visibility from user, with defaults
  const navVisibility = user?.navVisibility || {
    dashboard: true,
    clients: true,
    projects: true,
    team: true,
    admin: user?.isAdmin || false
  };

  // Build navigation items based on navVisibility settings from the user's role(s)
  const navigationItems = allNavigationItems.filter(item => {
    return navVisibility[item.id as keyof typeof navVisibility] === true;
  });

  return (
    <>
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden">
          <div className="fixed inset-0 z-40 flex">
            <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setMobileMenuOpen(false)} />
            <div className="relative flex w-full max-w-xs flex-1 flex-col bg-white border-r border-gray-200">
              <div className="absolute top-0 right-0 -mr-12 pt-2">
                <button
                  type="button"
                  className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <X className="h-6 w-6 text-white" />
                </button>
              </div>
              <div className="h-0 flex-1 overflow-y-auto pt-5 pb-4">
                <nav className="mt-5 space-y-1 px-2">
                  {navigationItems.map((item) => {
                    const Icon = item.Icon;
                    const isActive = activeView === item.id;
                    return (
                      <button
                        key={item.id}
                        onClick={() => {
                          onNavigate(item.id);
                          setMobileMenuOpen(false);
                        }}
                        className={`group flex w-full items-center rounded-md px-2 py-2 text-sm font-medium ${
                          isActive
                            ? 'bg-blue-600 text-white'
                            : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                        }`}
                      >
                        <Icon className="mr-3 h-5 w-5 flex-shrink-0" isActive={isActive} />
                        {item.label}
                      </button>
                    );
                  })}
                </nav>
              </div>
              <div className="flex flex-shrink-0 border-t border-gray-200 p-4">
                <div className="group block w-full flex-shrink-0">
                  <div className="flex items-center">
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">
                        {user?.first_name} {user?.last_name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {user?.roles?.[0]?.name || 'User'}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setSettingsOpen(true);
                        setMobileMenuOpen(false);
                      }}
                      className="ml-auto flex-shrink-0 p-1 text-gray-500 hover:text-gray-700"
                    >
                      <Settings className="h-5 w-5" />
                    </button>
                    <button
                      onClick={logout}
                      className="ml-2 flex-shrink-0 p-1 text-gray-500 hover:text-gray-700"
                    >
                      <LogOut className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Sidebar - Always Collapsed with Tooltips */}
      <div className="hidden lg:fixed lg:top-6 lg:left-6 lg:bottom-6 lg:z-50 lg:flex lg:flex-col lg:w-16">
        <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-sm px-2 pb-4 pt-4">
          {/* Logo */}
          <div className="flex h-24 shrink-0 items-center justify-center">
            <button
              onClick={() => onNavigate('dashboard')}
              className="relative group"
              title="Dashboard"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 90 65" width="90" height="65">
                <defs>
                  <linearGradient id="brandGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#8B5CF6"/>
                    <stop offset="50%" stopColor="#EC4899"/>
                    <stop offset="100%" stopColor="#8B5CF6"/>
                  </linearGradient>
                </defs>

                <g transform="translate(45, 32)">
                  <text x="0" y="-13"
                        fontFamily="-apple-system, BlinkMacSystemFont, 'SF Pro Display', system-ui, sans-serif"
                        fontSize="11"
                        fontWeight="300"
                        letterSpacing="0.15em"
                        fill="#71717A"
                        textAnchor="middle">BRAMBLE</text>

                  <text x="0" y="0"
                        fontFamily="-apple-system, BlinkMacSystemFont, 'SF Pro Display', system-ui, sans-serif"
                        fontSize="31"
                        fontWeight="600"
                        fill="url(#brandGradient)"
                        textAnchor="middle"
                        dominantBaseline="central">&amp;</text>

                  <text x="0" y="24"
                        fontFamily="-apple-system, BlinkMacSystemFont, 'SF Pro Display', system-ui, sans-serif"
                        fontSize="11"
                        fontWeight="300"
                        letterSpacing="0.15em"
                        fill="#71717A"
                        textAnchor="middle">BREEZE</text>
                </g>
              </svg>
            </button>
          </div>
          <nav className="flex flex-1 flex-col">
            <ul className="flex flex-1 flex-col gap-y-7">
              <li>
                <ul className="space-y-1">
                  {navigationItems.map((item) => {
                    const Icon = item.Icon;
                    const isActive = activeView === item.id;
                    return (
                      <li key={item.id}>
                        <button
                          onClick={() => onNavigate(item.id)}
                          className={`relative group w-12 h-12 flex items-center justify-center rounded-md transition-colors ${
                            isActive
                              ? 'bg-blue-600 text-white'
                              : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                          }`}
                          title={item.label}
                        >
                          <Icon className="h-5 w-5" isActive={isActive} />
                          <div className="absolute left-full ml-2 top-1/2 transform -translate-y-1/2 bg-gray-900 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">
                            {item.label}
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </li>
              <li className="mt-auto">
                <div className="relative group">
                  <div className="w-12 h-12 flex items-center justify-center">
                    <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center">
                      <span className="text-sm font-medium text-white">
                        {user?.first_name?.[0]}{user?.last_name?.[0]}
                      </span>
                    </div>
                  </div>
                  <div className="absolute left-full ml-2 top-1/2 transform -translate-y-1/2 bg-gray-900 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">
                    {user?.first_name} {user?.last_name}
                    <br />
                    {user?.roles?.[0]?.name || 'User'}
                  </div>
                </div>
                <button
                  onClick={() => setSettingsOpen(true)}
                  className="relative group w-12 h-12 flex items-center justify-center rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 mt-2"
                  title="Settings"
                >
                  <Settings className="h-5 w-5" />
                  <div className="absolute left-full ml-2 top-1/2 transform -translate-y-1/2 bg-gray-900 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">
                    Settings
                  </div>
                </button>
                <button
                  onClick={logout}
                  className="relative group w-12 h-12 flex items-center justify-center rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 mt-2"
                  title="Sign out"
                >
                  <LogOut className="h-5 w-5" />
                  <div className="absolute left-full ml-2 top-1/2 transform -translate-y-1/2 bg-gray-900 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">
                    Sign out
                  </div>
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Settings Modal */}
      <UserSettingsModal isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} />

      {/* Mobile menu button */}
      <div className="sticky top-0 z-40 flex items-center gap-x-4 bg-white px-4 py-4 shadow-sm sm:gap-x-6 sm:px-6 lg:hidden">
        <button
          type="button"
          className="text-gray-700"
          onClick={() => setMobileMenuOpen(true)}
        >
          <Menu className="h-6 w-6" />
        </button>
      </div>
    </>
  );
};